import { HttpClient } from "@angular/common/http";
import { Component, Input, OnInit } from "@angular/core";
import { FormArray, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { AppService } from "@root/app.service";
import { depositDoc, PlatformType } from "@root/config";
import { TranslateService } from "@root/shared/service/translate.service";
import { upperFirst, last, omitBy, range, isNil } from "lodash";
import { NzMessageService } from "ng-zorro-antd/message";
import { Observable } from "rxjs";

interface House {
  thirdparty_type: number;
  origin_house_id: string;
  [prop: string]: any;
}

@Component({
  selector: "property-detail-booking-settings",
  templateUrl: "./booking-settings.component.html",
  styleUrls: ["./booking-settings.component.scss"],
})
export class BookingSettingsComponent implements OnInit {
  _house;
  optionsOfNightlyRate = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"].map((s) => ({
    label: upperFirst(s.slice(0, 3)),
    value: s,
  }));

  optionsOfAirbnbLastMinute = range(1, 29).map((i) => ({
    label: i,
    value: i,
  }));
  optionsOfAirbnbEarlyBird = [
    28, 30, 56, 60, 84, 90, 112, 120, 140, 150, 168, 180, 196, 210, 224, 240, 252, 270, 280, 300, 308, 330, 336, 360,
    378, 392, 420, 440, 450, 480, 504, 540, 560, 588, 600,
  ].map((i) => ({
    label: i,
    value: i,
  }));

  optionsOfCheckinStartTime = [];
  optionsOfCheckinEndTime = [];
  optionsOfCheckoutTime = [];
  optionsOfAdvanceNotice = [];
  optionsOfAvailabilityWindow = [];
  optionsOfPreparationTime = [];
  optionsOfDaysOfCheck = [];

  @Input()
  get house(): House {
    return this._house;
  }
  set house(value) {
    if (value) {
      this._house = value;
      this.fetchOptionList();
      this.fetchDetail();
    }
  }

  get isAirbnb() {
    return this.house?.thirdparty_type === PlatformType.Airbnb;
  }

  get isVrbo() {
    return this.house?.thirdparty_type === PlatformType.Vrbo;
  }

  get isInternational() {
    return this.app.app.international;
  }

  get depositLinkDoc() {
    return depositDoc(this.isInternational);
  }

  vrboEnableNightlyRate = false;

  priceForm: UntypedFormGroup = this.fb.group({
    base_price: null,
    weekend_price: null,
    nightly_rate: this.fb.group({
      sunday: null,
      monday: null,
      tuesday: null,
      wednesday: null,
      thursday: null,
      friday: null,
      saturday: null,
    }), // vrbo
    weekly_discount: null,
    monthly_discount: null,
    early_bird_discount: this.fb.array([
      this.fb.group({
        discount: null,
        days: null,
      }),
    ]),
    last_minute_discount: this.fb.array([
      this.fb.group({
        discount: null,
        days: null,
      }),
    ]),
    // long_term_discount: this.fb.group({
    //   discount: null,
    //   days: null,
    // }),
    long_term_discount: this.fb.array([
      this.fb.group({
        discount: null,
        days: null,
      }),
    ]),
    cleaning_fee: null,
    extra_guest_fee: null,
    extra_guest_number: null, // vrbo
    pet_fee: null, // airbnb
    security_deposit: null,
    max_guests: null,
  });

  bookingForm: UntypedFormGroup = this.fb.group({
    check_in_start_time: null,
    check_in_end_time: null,
    check_out_before: null,
    instant_booking: null,
  });

  availabilityForm: UntypedFormGroup = this.fb.group({
    minimum_stay: null,
    maximum_stay: null,
    allow_rtb_above_max_nights: false,
    advance_notice: null,
    availability_window: null,
    preparation_time: null,
    days_of_week_check_in: null,
    days_of_week_check_out: null,
  });

  priceLoading = false;
  bookingLoading = false;
  availabilityLoading = false;

  readonly formArrayOptions = [
    {
      label: this.t.t("property-property_detail-booking_setting_html_长期折扣"),
      desc: this.t.t("property-property_detail-booking_setting_html_ltd_desc"),
      formArrayName: "long_term_discount",
    },
    {
      label: this.t.t("property-property_detail-booking_setting_html_早鸟折扣"),
      desc: this.t.t("property-property_detail-booking_setting_html_ebd_desc"),
      formArrayName: "early_bird_discount",
    },
    {
      label: this.t.t("property-property_detail-booking_setting_html_last_minute_discount"),
      desc: this.t.t("property-property_detail-booking_setting_html_lmd_desc"),
      formArrayName: "last_minute_discount",
    },
  ];
  trackFormArrayItem = (index) => {
    return index + ":" + Math.random();
  };

  get longTermDiscount() {
    return this.priceForm.get("long_term_discount") as FormArray;
  }

  constructor(
    private http: HttpClient,
    private fb: UntypedFormBuilder,
    private msg: NzMessageService,
    private t: TranslateService,
    private app: AppService,
  ) {}
  ngOnInit() {
    // if (this.isAirbnb || this.isVrbo) {
    //   this.fetchOptionList();
    // }
    // this.fetchDetail();
  }

  onAddItem(formArrayName: string) {
    const control = this.fb.group({
      discount: null,
      days: null,
    });
    (<FormArray>this.priceForm.get(formArrayName)).push(control);
    // formArray.push(control);
  }

  onRemoveItem(formArrayName: string, index: number) {
    (<FormArray>this.priceForm.get(formArrayName)).removeAt(index);
  }

  onSavePrice() {
    const data = this.filterEmptyValues(this.priceForm.value);
    /** vrbo base_price和nightly_rate互斥 */
    if (this.vrboEnableNightlyRate) {
      if (Object.values(data.nightly_rate).every((data) => data === null)) {
        /** 如果nightly_rate的所以值都没填，则不传该字段 */
        data.nightly_rate = null;
      } else {
        /** 否则就不传base_price */
        data.base_price = null;
      }
    }
    this.priceLoading = true;
    this.requestUpdate(data).subscribe(() => {
      this.priceLoading = false;
    });
  }

  onSaveBookingSettings() {
    const data = this.filterEmptyValues(this.bookingForm.value);
    this.bookingLoading = true;
    this.requestUpdate(data).subscribe(() => {
      this.bookingLoading = false;
    });
  }

  onSaveAvailability() {
    const data = this.filterEmptyValues(this.availabilityForm.value);
    if (this.isAirbnb) {
      Object.assign(data, { advance_notice: last(data.advance_notice) });
    }
    this.availabilityLoading = true;
    this.requestUpdate(data).subscribe(() => {
      this.availabilityLoading = false;
    });
  }

  private fetchOptionList() {
    this.http.get("/api/docking/airbnb/select_option_list").subscribe((rst: any) => {
      const data = rst.data;
      const { airbnb, vrbo } = data;
      if (this.isAirbnb) {
        this.optionsOfAdvanceNotice = airbnb.advance_notice?.map((item) => ({
          ...item,
          value: Array.isArray(item.value) ? -1 : item.value,
          children: Array.isArray(item.value) ? item.value.map((sub) => ({ ...sub, isLeaf: true })) : null,
          isLeaf: Array.isArray(item.value) ? false : true,
        }));
        console.log("optionsOfAdvanceNotice: ", this.optionsOfAdvanceNotice);
        this.optionsOfAvailabilityWindow = airbnb.availability_window;
        this.optionsOfCheckinStartTime = airbnb.check_in_start_time;
        this.optionsOfCheckinEndTime = airbnb.check_in_end_time;
        this.optionsOfCheckoutTime = airbnb.check_out_before;
        this.optionsOfDaysOfCheck = airbnb.days_of_week_check_in;
        this.optionsOfPreparationTime = airbnb.preparation_time;
      } else {
        this.optionsOfAdvanceNotice = vrbo.advance_notice;
        this.optionsOfAvailabilityWindow = vrbo.availability_window;
        this.optionsOfCheckinStartTime = vrbo.check_in_start_time;
        this.optionsOfCheckoutTime = vrbo.check_out_before;
      }
    });
  }

  private fetchDetail() {
    const params = {
      type: this.house?.house_type_id ? "house_type" : "house",
      origin_house_id: this.house.origin_house_id,
    };
    if (this.isAirbnb) {
      this.fetchAirbnbDetail(params);
    } else {
      this.fetchVrboDetail(params);
    }
  }

  private fetchAirbnbDetail(params) {
    this.http.get("/api/docking/airbnb/price_and_rule_detail", { params }).subscribe((rst: any) => {
      if (rst.error_code === 0) {
        const data = rst.data;
        console.log("🚀 ~ BookingSettingsComponent ~ this.http.get ~ data:", data);
        this.priceForm.patchValue(data);
        this.bookingForm.patchValue(data);
        this.availabilityForm.patchValue(data);

        this.initFormArray("long_term_discount", data.long_term_discount);
        this.initFormArray("early_bird_discount", data.early_bird_discount);
        this.initFormArray("last_minute_discount", data.last_minute_discount);

        /** 特殊处理advance notice的二级选项 */
        if (data.advance_notice <= 23) {
          this.availabilityForm.patchValue({ advance_notice: [-1, data.advance_notice] });
        }
      }
    });
  }

  private fetchVrboDetail(params) {
    this.http.get("/api/docking/vrbo/price_and_rule_detail", { params }).subscribe((rst: any) => {
      if (rst.error_code === 0) {
        const data = rst.data;
        this.vrboEnableNightlyRate = !!data.nightly_rate;
        this.priceForm.patchValue(data);
        this.bookingForm.patchValue(data);
        this.availabilityForm.patchValue(data);
      }
    });
  }

  private requestUpdate(data) {
    console.log("data: ", data);
    const params = {
      type: this.house?.house_type_id ? "house_type" : "house",
      origin_house_id: this.house.origin_house_id,
      list: { ...data },
    };
    let request$;
    if (this.isAirbnb) {
      request$ = this.requestUpdateAirbnb(params);
    } else {
      request$ = this.requestUpdateVrbo(params);
    }

    return new Observable((observer) => {
      request$.subscribe((rst: any) => {
        if (rst.error_code === 0) {
          this.msg.success(this.t.t("success.save"));
        }
        observer.next(null);
        observer.complete();
      });
    });
  }

  private requestUpdateAirbnb(params) {
    const list = params.list;
    console.log("list: ", list);
    if (list.long_term_discount) {
      list.long_term_discount = this.parseEmptyFormArrayValue(list.long_term_discount);
    }
    if (list.last_minute_discount) {
      list.last_minute_discount = this.parseEmptyFormArrayValue(list.last_minute_discount);
    }
    if (list.early_bird_discount) {
      list.early_bird_discount = this.parseEmptyFormArrayValue(list.early_bird_discount);
    }
    return this.http.post("/api/docking/airbnb/edit_price_and_rule", params);
  }

  private requestUpdateVrbo(params) {
    return this.http.post("/api/docking/vrbo/edit_price_and_rule", params);
  }

  private filterEmptyValues(data) {
    return omitBy(data, (value) => value === null || value === undefined || value === "");
  }

  private initFormArray(field: string, value: any[]) {
    const createGroup = (data?) =>
      this.fb.group({
        discount: data?.discount,
        days: data?.days,
      });

    const formArray = this.priceForm.get(field) as FormArray;
    formArray.clear();

    /**
     * 加一个延时，是为了正常触发 angular 的变化检测
     * v17 的控制流语法中，angular 强制 for循环带有一个 track，以便进行列表性能优化，默认 track:index
     * 这就会导致一个问题：formArray 中原本就有一个元算，此时继续添加元素，index 为 0 的元素由于 index 没有发生变化，所以不会触发变化检测，导致表单数据没有更新
     * 这里先清空列表，强制触发一次渲染，然后重新初始化列表，以便正常触发变化检测
     *
     * 另一个解决办法是使用老版本的循环语法，且不添加 trackBy 函数即可
     */
    setTimeout(() => {
      if (value.length === 0) {
        formArray.push(createGroup());
      } else {
        value.forEach((item) => {
          formArray.push(createGroup(item));
        });
      }
    }, 0);
  }

  /**
   * 过滤掉数组中所有字段都为空的值
   * 比如[{ discount: null, days: null }] 认为是[]
   */
  private parseEmptyFormArrayValue(value: any[]) {
    return value?.filter((item) => !isNil(item.discount) && !isNil(item.days));
  }
}
